"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCartStore } from "@/features/cart/components";
import type { Cart } from "@/features/cart/types/cart.type";
import type { CartMergeRequest } from "@/features/cart/types/cart-merge.type";
import { ApiError } from "@/lib/helpers/errors";

export const useMergeCart = () => {
  const queryClient = useQueryClient();
  const setCart = useCartStore((state) => state.setCart);
  const setCartId = useCartStore((state) => state.setCartId);
  const clearDeliveryCost = useCartStore((state) => state.clearDeliveryCost);

  return useMutation({
    mutationFn: async (body: CartMergeRequest) => {
      const response = await fetch("/api/cart/merge", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = (await response.json()) as Cart | { error?: string };

      if (!response.ok) {
        const code = (data as { error?: string }).error ?? "cart_merge_failed";
        throw new ApiError(code, response.status, data);
      }

      return { cart: data as Cart, targetCartId: body.targetCartId };
    },
    onSuccess: ({ cart, targetCartId }) => {
      setCartId(targetCartId);
      setCart(cart);
      clearDeliveryCost();
      queryClient.invalidateQueries({ queryKey: ["cart", targetCartId] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};
